import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  FormControlLabel,
  InputAdornment,
  Paper,
  Stack,
  Switch,
  Table,
  TextField,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import PeopleIcon from '@mui/icons-material/People';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';

const CLOUDINARY_CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'dml5dugde';
const CLOUDINARY_UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || 'locsy_business_profiles';
const hasCloudinaryConfig = Boolean(CLOUDINARY_CLOUD_NAME && CLOUDINARY_UPLOAD_PRESET);

const getAuthHeaders = () => {
  const token = localStorage.getItem('admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  if (typeof window !== 'undefined' && (window.location.hostname.includes('ondigitalocean.app') || window.location.hostname.includes('services.knls.in'))) {
    return '/api';
  }
  return 'http://localhost:3000/api';
};

const API_BASE_URL = getApiBaseUrl();

const uploadThumbnailToCloudinary = async (file) => {
  if (!hasCloudinaryConfig) {
    throw new Error('Cloudinary is not configured. Set REACT_APP_CLOUDINARY_CLOUD_NAME and REACT_APP_CLOUDINARY_UPLOAD_PRESET.');
  }

  const formData = new FormData();
  formData.append('file', file);
  formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
  formData.append('folder', 'lms_course_thumbnails');

  const cleanAxios = axios.create();
  if (cleanAxios.defaults.headers.common) {
    delete cleanAxios.defaults.headers.common.Authorization;
    delete cleanAxios.defaults.headers.common.authorization;
  }

  const response = await cleanAxios.post(
    `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
    formData,
  );

  const secureUrl = response?.data?.secure_url || response?.data?.url || '';
  if (!secureUrl) {
    throw new Error('Thumbnail upload failed');
  }
  return secureUrl;
};

/**
 * Course price, thumbnail, and enrollment visibility for a single course.
 * Used on LMS Builder, Course Management, and the dedicated display-settings page.
 */
const CourseDisplaySettingsPanel = ({ courseCode, compact = false, onFeeSaved }) => {
  const [loading, setLoading] = useState(true);
  const [thumbnailUploading, setThumbnailUploading] = useState(false);
  const [feeSaving, setFeeSaving] = useState(false);
  const [feeDraft, setFeeDraft] = useState('');
  const [pricingMode, setPricingMode] = useState('paid');
  const lastPaidFeeRef = useRef('');
  const [enrolledStudentsOpen, setEnrolledStudentsOpen] = useState(false);
  const [enrolledStudentsLoading, setEnrolledStudentsLoading] = useState(false);
  const [enrolledStudents, setEnrolledStudents] = useState([]);
  const [meta, setMeta] = useState({
    courseName: '',
    thumbnailUrl: '',
    enrollmentCount: 0,
    showEnrollmentCount: true,
    showPracticeIde: false,
    fee: 0,
  });

  const loadCourseMeta = useCallback(async () => {
    if (!courseCode) return;
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/lms/courses/${courseCode}/builder?_t=${Date.now()}`,
        { headers: getAuthHeaders() },
      );
      if (response.data?.success) {
        const nextFee = Number(response.data.fee);
        const fee = Number.isFinite(nextFee) && nextFee >= 0 ? nextFee : 0;
        setPricingMode(fee > 0 ? 'paid' : 'free');
        setFeeDraft(fee > 0 ? String(fee) : '');
        if (fee > 0) lastPaidFeeRef.current = String(fee);
        setMeta({
          courseName: response.data.courseName || courseCode,
          thumbnailUrl: response.data.thumbnailUrl || '',
          enrollmentCount: Number(response.data.enrollmentCount) || 0,
          showEnrollmentCount: response.data.showEnrollmentCount !== false,
          showPracticeIde: response.data.showPracticeIde === true,
          fee,
        });
      }
    } catch (error) {
      console.error('Failed to load course display settings:', error);
      toast.error('Failed to load course display settings');
    } finally {
      setLoading(false);
    }
  }, [courseCode]);

  useEffect(() => {
    loadCourseMeta();
  }, [loadCourseMeta]);

  const saveCourseField = async (payload) => {
    await axios.put(
      `${API_BASE_URL}/students/courses/${courseCode}`,
      payload,
      { headers: getAuthHeaders() },
    );
  };

  const handleThumbnailSelect = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    const mime = String(file.type || '').toLowerCase();
    if (!mime.startsWith('image/')) {
      toast.error('Please select a JPG, PNG, or WebP image');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Thumbnail must be under 5 MB');
      return;
    }

    try {
      setThumbnailUploading(true);
      const url = await uploadThumbnailToCloudinary(file);
      await saveCourseField({ thumbnail_url: url });
      setMeta((prev) => ({ ...prev, thumbnailUrl: url }));
      toast.success('Course thumbnail uploaded');
    } catch (error) {
      console.error('Thumbnail upload failed:', error);
      toast.error(error.response?.data?.message || error.message || 'Failed to upload thumbnail');
    } finally {
      setThumbnailUploading(false);
    }
  };

  const handleRemoveThumbnail = async () => {
    try {
      setThumbnailUploading(true);
      await saveCourseField({ thumbnail_url: '' });
      setMeta((prev) => ({ ...prev, thumbnailUrl: '' }));
      toast.success('Thumbnail removed');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to remove thumbnail');
    } finally {
      setThumbnailUploading(false);
    }
  };

  const handleShowEnrollmentCountChange = async (event) => {
    const checked = event.target.checked;
    try {
      await saveCourseField({ show_enrollment_count: checked });
      setMeta((prev) => ({ ...prev, showEnrollmentCount: checked }));
      toast.success(
        checked
          ? 'Enrollment count will show on student app'
          : 'Enrollment count hidden from student app',
      );
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update setting');
    }
  };

  const parsedFee = Number(feeDraft);
  const feeIsValid = Number.isFinite(parsedFee) && parsedFee > 0;
  const feeUnchanged = feeIsValid && parsedFee === Number(meta.fee);

  const saveFeeValue = async (fee) => {
    try {
      setFeeSaving(true);
      await saveCourseField({ fee });
      setMeta((prev) => ({ ...prev, fee }));
      setPricingMode(fee > 0 ? 'paid' : 'free');
      setFeeDraft(fee > 0 ? String(fee) : (lastPaidFeeRef.current || ''));
      if (fee > 0) lastPaidFeeRef.current = String(fee);
      onFeeSaved?.(fee);
      toast.success(fee === 0 ? 'Course is now free' : `Course price saved: ₹${fee.toLocaleString('en-IN')}`);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save course price');
    } finally {
      setFeeSaving(false);
    }
  };

  const handleSaveFee = async () => {
    if (!feeIsValid) {
      toast.error('Enter a price above ₹0, or choose Free.');
      return;
    }
    await saveFeeValue(Math.round(parsedFee * 100) / 100);
  };

  const handleChooseFree = async () => {
    if (Number(meta.fee) === 0) {
      setPricingMode('free');
      return;
    }
    await saveFeeValue(0);
  };

  const handleChoosePaid = () => {
    setPricingMode('paid');
    setFeeDraft(lastPaidFeeRef.current || (Number(meta.fee) > 0 ? String(meta.fee) : ''));
  };

  const handleShowPracticeIdeChange = async (event) => {
    const checked = event.target.checked;
    try {
      await saveCourseField({ show_practice_ide: checked });
      setMeta((prev) => ({ ...prev, showPracticeIde: checked }));
      toast.success(
        checked
          ? 'Practice IDE card enabled for this course'
          : 'Practice IDE card hidden for this course',
      );
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update setting');
    }
  };

  const loadEnrolledStudents = async () => {
    if (!courseCode) return;
    try {
      setEnrolledStudentsLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/students/courses/${courseCode}/enrolled-students`,
        { headers: getAuthHeaders() },
      );
      if (response.data?.success) {
        setEnrolledStudents(response.data.students || []);
        setMeta((prev) => ({
          ...prev,
          enrollmentCount: Number(response.data.total) || prev.enrollmentCount,
        }));
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to load enrolled students');
    } finally {
      setEnrolledStudentsLoading(false);
    }
  };

  const handleToggleEnrolledStudents = async () => {
    const nextOpen = !enrolledStudentsOpen;
    setEnrolledStudentsOpen(nextOpen);
    if (nextOpen && enrolledStudents.length === 0) {
      await loadEnrolledStudents();
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 3 }}>
        <CircularProgress size={22} />
        <Typography variant="body2">Loading display settings...</Typography>
      </Box>
    );
  }

  return (
    <Card
      id="course-display-settings"
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        border: '2px solid',
        borderColor: 'primary.light',
        boxShadow: '0 8px 24px rgba(102, 126, 234, 0.15)',
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h6" fontWeight={800} color="primary.main">
              Student App Display Settings
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Set the price students pay, then the thumbnail and enrollment details shown on My Learning cards.
            </Typography>
          </Box>

          <Box
            id={`course-price-${courseCode}`}
            sx={{
              p: compact ? 1.5 : 2,
              borderRadius: 2,
              bgcolor: '#f8fafc',
              border: '1px solid',
              borderColor: '#dbe3f0',
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }} flexWrap="wrap" useFlexGap>
              <AttachMoneyIcon fontSize="small" color="primary" />
              <Typography variant="subtitle2" fontWeight={800}>
                Course Price
              </Typography>
              <Chip
                size="small"
                color={Number(meta.fee) > 0 ? 'success' : 'default'}
                label={Number(meta.fee) > 0 ? `₹${Number(meta.fee).toLocaleString('en-IN')}` : 'Free'}
              />
            </Stack>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
              Choose Free for no payment, or Paid and enter the amount students pay to enroll.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }} flexWrap="wrap" useFlexGap>
              <Stack direction="row" spacing={1}>
                <Button
                  variant={pricingMode === 'free' ? 'contained' : 'outlined'}
                  color="success"
                  onClick={handleChooseFree}
                  disabled={feeSaving}
                  sx={{ minWidth: 96 }}
                >
                  Free
                </Button>
                <Button
                  variant={pricingMode === 'paid' ? 'contained' : 'outlined'}
                  onClick={handleChoosePaid}
                  disabled={feeSaving}
                  sx={{ minWidth: 96 }}
                >
                  Paid
                </Button>
              </Stack>
              {pricingMode === 'paid' ? (
                <>
                  <TextField
                    id={`course-price-input-${courseCode}`}
                    label="Price (₹)"
                    type="number"
                    size="small"
                    value={feeDraft}
                    onChange={(event) => setFeeDraft(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        handleSaveFee();
                      }
                    }}
                    inputProps={{ min: 1, step: 1 }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    }}
                    sx={{ width: { xs: '100%', sm: 220 } }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleSaveFee}
                    disabled={feeSaving || !feeIsValid || feeUnchanged}
                    startIcon={feeSaving ? <CircularProgress size={16} color="inherit" /> : <AttachMoneyIcon />}
                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                  >
                    Save price
                  </Button>
                </>
              ) : (
                <Typography variant="body2" color="success.main" fontWeight={700}>
                  Students can enroll with no payment.
                </Typography>
              )}
            </Stack>
          </Box>

          {!hasCloudinaryConfig && (
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              Cloudinary is not configured. Thumbnail upload needs REACT_APP_CLOUDINARY_CLOUD_NAME and REACT_APP_CLOUDINARY_UPLOAD_PRESET.
            </Alert>
          )}

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                <ImageIcon fontSize="small" color="primary" />
                Course Thumbnail (16:9)
              </Typography>
              <Box
                sx={{
                  width: compact ? '100%' : 280,
                  maxWidth: '100%',
                  aspectRatio: '16 / 9',
                  borderRadius: 2,
                  overflow: 'hidden',
                  bgcolor: '#f3f4f6',
                  border: '2px dashed',
                  borderColor: meta.thumbnailUrl ? 'transparent' : '#94a3b8',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 1.5,
                }}
              >
                {meta.thumbnailUrl ? (
                  <Box
                    component="img"
                    src={meta.thumbnailUrl}
                    alt={meta.courseName}
                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <Stack alignItems="center" spacing={0.5}>
                    <ImageIcon sx={{ fontSize: 40, color: '#94a3b8' }} />
                    <Typography variant="caption" color="text.secondary">
                      No thumbnail uploaded
                    </Typography>
                  </Stack>
                )}
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button
                  variant="contained"
                  component="label"
                  startIcon={thumbnailUploading ? <CircularProgress size={16} color="inherit" /> : <CloudUploadIcon />}
                  disabled={thumbnailUploading || !hasCloudinaryConfig}
                >
                  {meta.thumbnailUrl ? 'Change Thumbnail' : 'Upload Thumbnail'}
                  <input type="file" hidden accept="image/*" onChange={handleThumbnailSelect} />
                </Button>
                {meta.thumbnailUrl && (
                  <Button variant="outlined" color="error" disabled={thumbnailUploading} onClick={handleRemoveThumbnail}>
                    Remove
                  </Button>
                )}
              </Stack>
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                <PeopleIcon fontSize="small" color="primary" />
                Enrollment Visibility
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                <strong>{meta.courseName}</strong> — {meta.enrollmentCount.toLocaleString()} students enrolled
                {meta.showEnrollmentCount ? ' (visible on app)' : ' (hidden on app)'}
              </Typography>
              <FormControlLabel
                sx={{ alignItems: 'flex-start', mb: 1 }}
                control={
                  <Switch
                    checked={meta.showEnrollmentCount}
                    onChange={handleShowEnrollmentCountChange}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={700}>
                      Show enrollment count on student app
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      When OFF, &quot;X learners&quot; will not appear on My Learning cards in the web portal and mobile app.
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                sx={{ alignItems: 'flex-start', mb: 1 }}
                control={
                  <Switch
                    checked={meta.showPracticeIde}
                    onChange={handleShowPracticeIdeChange}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={700}>
                      Show Practice IDE on student course page
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Default is OFF. Enable this per course (for example PGDCA or Python) to show the Practice IDE card.
                    </Typography>
                  </Box>
                }
              />
              <Button size="small" variant="text" onClick={handleToggleEnrolledStudents} sx={{ px: 0 }}>
                {enrolledStudentsOpen ? 'Hide enrolled students list' : 'View enrolled students list (admin only)'}
              </Button>
              <Collapse in={enrolledStudentsOpen}>
                <Box sx={{ mt: 1 }}>
                  {enrolledStudentsLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                      <CircularProgress size={18} />
                      <Typography variant="body2">Loading students...</Typography>
                    </Box>
                  ) : enrolledStudents.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No students enrolled yet.
                    </Typography>
                  ) : (
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 260 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Phone</TableCell>
                            <TableCell>Student ID</TableCell>
                            <TableCell>Joined</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {enrolledStudents.map((student) => (
                            <TableRow key={student.id || student.phone}>
                              <TableCell>{student.name || '—'}</TableCell>
                              <TableCell>{student.phone || '—'}</TableCell>
                              <TableCell>{student.student_id || '—'}</TableCell>
                              <TableCell>
                                {student.joining_date
                                  ? new Date(student.joining_date).toLocaleDateString()
                                  : '—'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              </Collapse>
            </Box>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default CourseDisplaySettingsPanel;
